import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { useTRPC } from '@/trpc/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/dashboard/employee/leaves/')({
  component: RouteComponent,
})

const STATUS_BADGE_CLASSNAME: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  approved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
  cancelled: 'bg-gray-100 text-gray-600 dark:bg-gray-900 dark:text-gray-400',
}

function formatDate(iso: string): string {
  return DateTime.fromISO(iso).toFormat('MMM d, yyyy')
}

interface RequestFormState {
  leaveTypeId: string
  startDate: string
  endDate: string
  isHalfDay: boolean
  reason: string
  attachmentFile: File | null
}

const EMPTY_REQUEST_FORM: RequestFormState = {
  leaveTypeId: '',
  startDate: '',
  endDate: '',
  isHalfDay: false,
  reason: '',
  attachmentFile: null,
}

function RouteComponent() {
  const api = useTRPC()
  const queryClient = useQueryClient()

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [form, setForm] = useState<RequestFormState>(EMPTY_REQUEST_FORM)

  const { data: leaveTypes, isPending: isPendingLeaveTypes } = useQuery({
    ...api.leave.getLeaveTypesBusiness.queryOptions(),
  })

  const { data: balances, isPending: isPendingBalances } = useQuery({
    ...api.leave.getCurrentLeaveBalance.queryOptions({}),
  })

  const { data: requests, isPending: isPendingRequests } = useQuery({
    ...api.leave.getMyLeaveRequests.queryOptions({}),
  })

  const createRequestMutation = useMutation({
    ...api.leave.createLeaveRequest.mutationOptions({
      onSuccess: () => {
        toast.success('Leave request submitted')
        queryClient.invalidateQueries({ queryKey: api.leave.getMyLeaveRequests.queryKey() })
        setForm(EMPTY_REQUEST_FORM)
        setIsDialogOpen(false)
      },
      onError: (error) => {
        toast.error('Failed to submit leave request', { description: error.message })
      },
    }),
  })

  // Phase I / Step I1 — self-service withdrawal of a still-pending request. Only ever
  // enabled for 'pending' rows (see the Cancel button below); the balance query is
  // invalidated too even though a pending cancel never touches the ledger, since it's
  // cheap and keeps this correct if that ever changes.
  const cancelRequestMutation = useMutation({
    ...api.leave.cancelLeaveRequest.mutationOptions({
      onSuccess: () => {
        toast.success('Leave request cancelled')
        queryClient.invalidateQueries({ queryKey: api.leave.getMyLeaveRequests.queryKey() })
        queryClient.invalidateQueries({ queryKey: api.leave.getCurrentLeaveBalance.queryKey() })
      },
      onError: (error) => {
        toast.error('Failed to cancel leave request', { description: error.message })
      },
    }),
  })

  const isFormValid =
    form.leaveTypeId !== '' && form.startDate !== '' && (form.isHalfDay || form.endDate !== '')

  function handleSubmit() {
    createRequestMutation.mutate({
      leaveTypeId: form.leaveTypeId,
      startDate: form.startDate,
      endDate: form.isHalfDay ? form.startDate : form.endDate,
      isHalfDay: form.isHalfDay,
      reason: form.reason || null,
      // No file-upload pipeline wired yet — attachmentFile is collected in the form for
      // UI completeness but not sent anywhere.
      attachmentUrl: null,
    })
  }

  // getCurrentLeaveBalance only returns a net SUM per leave type (Step C3's ledger
  // design — no separate entitlement/used tracking), so this only carries a balance
  // number, not a used/entitlement breakdown the way Phase A's dummy card did.
  const balancesWithNames = (balances ?? []).map((balance) => {
    const leaveType = leaveTypes?.find((type) => type.leaveTypeId === balance.leaveTypeId)
    return {
      leaveTypeId: balance.leaveTypeId,
      name: leaveType?.name ?? 'Unknown',
      balance: Number(balance.balance),
    }
  })

  return (
    <DashboardShell>
      <DashboardHeader heading="Leaves" text="View your leave balances and request time off.">
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Request Leave
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Request Leave</DialogTitle>
              <DialogDescription>Submit a new leave request for approval.</DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-2">
              <div className="grid gap-2">
                <Label>Leave type</Label>
                <Select
                  value={form.leaveTypeId}
                  onValueChange={(value) => setForm((prev) => ({ ...prev, leaveTypeId: value }))}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={isPendingLeaveTypes ? 'Loading...' : 'Select a leave type'}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {(leaveTypes ?? []).map((type) => (
                      <SelectItem key={type.leaveTypeId} value={type.leaveTypeId}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                <div className="grid gap-0.5">
                  <Label>Half-day</Label>
                  <span className="text-xs text-muted-foreground">
                    Toggle if this request is for half a day only
                  </span>
                </div>
                <Switch
                  checked={form.isHalfDay}
                  onCheckedChange={(checked) =>
                    setForm((prev) => ({ ...prev, isHalfDay: checked, endDate: '' }))
                  }
                />
              </div>

              {form.isHalfDay ? (
                <div className="grid gap-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={form.startDate}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, startDate: event.target.value }))
                    }
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>Start date</Label>
                    <Input
                      type="date"
                      value={form.startDate}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, startDate: event.target.value }))
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>End date</Label>
                    <Input
                      type="date"
                      value={form.endDate}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, endDate: event.target.value }))
                      }
                    />
                  </div>
                </div>
              )}

              <div className="grid gap-2">
                <Label>Reason</Label>
                <Textarea
                  placeholder="Optional details for your approver"
                  value={form.reason}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, reason: event.target.value }))
                  }
                />
              </div>

              <div className="grid gap-2">
                <Label>Attachment (optional)</Label>
                <Input
                  type="file"
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      attachmentFile: event.target.files?.[0] ?? null,
                    }))
                  }
                />
                <span className="text-xs text-muted-foreground">
                  Attachment uploads aren&apos;t wired up yet — this file won&apos;t be saved.
                </span>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                disabled={!isFormValid || createRequestMutation.isPending}
                onClick={handleSubmit}
              >
                {createRequestMutation.isPending ? 'Submitting...' : 'Submit Request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DashboardHeader>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {isPendingBalances || isPendingLeaveTypes ? (
          <>
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </>
        ) : (
          balancesWithNames.map((balance) => (
            <Card
              key={balance.leaveTypeId}
              className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700"
            >
              <CardHeader>
                <CardTitle className="text-base font-semibold">{balance.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-black tracking-tight">{balance.balance}</p>
                <p className="text-xs text-muted-foreground tracking-wide uppercase">
                  days remaining
                </p>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Card className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-base font-semibold">Request History</CardTitle>
          <CardDescription>Your past and pending leave requests.</CardDescription>
        </CardHeader>
        <CardContent>
          {isPendingRequests ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Leave Type</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Half-day</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(requests ?? []).map((request) => (
                  <TableRow key={request.leaveRequestId}>
                    <TableCell>{request.leaveType.name}</TableCell>
                    <TableCell>
                      {request.isHalfDay
                        ? formatDate(request.startDate)
                        : `${formatDate(request.startDate)} - ${formatDate(request.endDate)}`}
                    </TableCell>
                    <TableCell>{request.isHalfDay ? 'Yes' : 'No'}</TableCell>
                    <TableCell>
                      <Badge className={STATUS_BADGE_CLASSNAME[request.status]} variant="outline">
                        {request.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {request.status === 'pending' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={cancelRequestMutation.isPending}
                          onClick={() =>
                            cancelRequestMutation.mutate({
                              leaveRequestId: request.leaveRequestId,
                            })
                          }
                        >
                          Cancel
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </DashboardShell>
  )
}
